// assets/custom-product-carousel.js

(function () {
  'use strict';

  const SELECTORS = {
    root:      '[data-component="product-carousel"]',
    track:     '[data-pc-track]',
    slide:     '[data-pc-slide]',
    prev:      '[data-pc-prev]',
    next:      '[data-pc-next]',
    dots:      '[data-pc-dots]',
    atcBtn:    '[data-pc-atc]',
    qvBtn:     '[data-pc-quickview]',
  };

  class ProductCarousel {
    constructor(el) {
      this.el            = el;
      this.track         = el.querySelector(SELECTORS.track);
      this.slides        = Array.from(el.querySelectorAll(SELECTORS.slide));
      this.prevBtns      = Array.from(el.querySelectorAll(SELECTORS.prev));
      this.nextBtns      = Array.from(el.querySelectorAll(SELECTORS.next));
      this.dotsContainer = el.querySelector(SELECTORS.dots);

      this.style     = el.dataset.carouselStyle || 'scroll-snap';
      this.isGrid    = this.style === 'grid';
      this.loop      = el.dataset.loop === 'true';
      this.autoplay  = el.dataset.autoplay === 'true';
      this.speed     = parseInt(el.dataset.autoplaySpeed || '4', 10) * 1000;

      this._index    = 0;
      this._timer    = null;
      this._ticking  = false;
      this._dots     = [];

      this._init();
    }

    _init() {
      if (this.isGrid) return;
      this._buildDots();
      this._bindArrows();
      this._bindScroll();
      this._bindDrag();
      this._bindKeyboard();
      this._updateArrows();
      this._updateGrabbable();
      this._initAutoplay();
      this._bindATC();
      this._bindQuickView();
    }

    // ── Dots ────────────────────────────────────────────────────────────────
    _buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      if (this.slides.length <= 1) return;
      this.slides.forEach((_, i) => {
        const btn = document.createElement('button');
        btn.className = 'hbk-pc__dot' + (i === 0 ? ' hbk-pc__dot--active' : '');
        btn.type = 'button';
        btn.setAttribute('aria-label', `Slide ${i + 1}`);
        btn.addEventListener('click', () => this._goTo(i));
        this.dotsContainer.appendChild(btn);
      });
      this._dots = Array.from(this.dotsContainer.querySelectorAll('.hbk-pc__dot'));
    }

    _syncDots() {
      this._dots.forEach((d, i) => d.classList.toggle('hbk-pc__dot--active', i === this._index));
    }

    // ── Arrows ──────────────────────────────────────────────────────────────
    _bindArrows() {
      this.prevBtns.forEach(btn => btn.addEventListener('click', () => this._step(-1)));
      this.nextBtns.forEach(btn => btn.addEventListener('click', () => this._step(1)));
    }

    _step(dir) {
      const next = this._index + dir;
      if (this.loop) {
        this._goTo((next + this.slides.length) % this.slides.length);
      } else {
        this._goTo(Math.max(0, Math.min(next, this.slides.length - 1)));
      }
    }

    _goTo(index) {
      if (!this.slides[index]) return;
      this._index = index;
      const slide = this.slides[index];
      if (this.track) {
        this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
      this._syncDots();
      this._updateArrows();
    }

    _updateArrows() {
      if (this.loop) return;
      const atStart = this._index === 0;
      const atEnd   = this._index >= this.slides.length - 1;
      this.prevBtns.forEach(btn => { btn.disabled = atStart; });
      this.nextBtns.forEach(btn => { btn.disabled = atEnd; });
    }

    // ── Scroll tracking ─────────────────────────────────────────────────────
    _bindScroll() {
      if (!this.track) return;
      this.track.addEventListener('scroll', () => {
        if (this._ticking) return;
        requestAnimationFrame(() => {
          this._detectVisible();
          this._ticking = false;
        });
        this._ticking = true;
      }, { passive: true });
    }

    _detectVisible() {
      const trackLeft = this.track.getBoundingClientRect().left;
      let closest = 0, minDist = Infinity;
      this.slides.forEach((slide, i) => {
        const dist = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      if (closest !== this._index) {
        this._index = closest;
        this._syncDots();
        this._updateArrows();
      }
      this._updateGrabbable();
    }

    // ── Mouse drag-to-scroll (desktop) ────────────────────────────────────────
    // Browsers only scroll an overflow container via touch/trackpad/wheel — a mouse
    // drag does nothing natively, so wire it up manually. Touch stays native.
    _updateGrabbable() {
      if (!this.track) return;
      const overflowing = this.track.scrollWidth > this.track.clientWidth + 1;
      this.track.classList.toggle('hbk-pc__track--grabbable', overflowing);
    }

    _bindDrag() {
      if (!this.track) return;
      let isDown = false, startX = 0, startScroll = 0, moved = false;
      const THRESHOLD = 4;

      // NOTE: do NOT use setPointerCapture here. Capturing the pointer on the track
      // retargets the trailing click to the track, so clicks on card links / the
      // Add-to-cart button never reach them (navigation + ATC silently fail). Tracking
      // the drag with window listeners keeps clicks on their real targets.
      const onMove = (e) => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > THRESHOLD) moved = true;
        if (moved) { e.preventDefault(); this.track.scrollLeft = startScroll - dx; }
      };
      const onUp = () => {
        if (!isDown) return;
        isDown = false;
        this.track.classList.remove('is-dragging');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      const onDown = (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return; // touch = native scroll
        if (e.button !== 0) return;                              // left button only
        isDown = true; moved = false;
        startX = e.clientX; startScroll = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      };
      // Swallow the post-drag click so dragging a card doesn't open it / trigger ATC.
      const onClick = (e) => {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      };
      const onDragStart = (e) => e.preventDefault();

      this.track.addEventListener('pointerdown', onDown);
      this.track.addEventListener('click', onClick, true);
      this.track.addEventListener('dragstart', onDragStart);
      this._drag = { onDown, onMove, onUp, onClick, onDragStart };
    }

    // ── Keyboard ────────────────────────────────────────────────────────────
    _bindKeyboard() {
      this.el.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._step(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._step(1); }
      });
    }

    // ── Autoplay ────────────────────────────────────────────────────────────
    _initAutoplay() {
      if (!this.autoplay) return;
      const io = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          entry.isIntersecting ? this._startAutoplay() : this._stopAutoplay();
        });
      }, { threshold: 0.3 });
      io.observe(this.el);
      this._io = io;
    }

    _startAutoplay() {
      this._stopAutoplay();
      this._timer = setInterval(() => this._step(1), this.speed);
    }

    _stopAutoplay() {
      clearInterval(this._timer);
      this._timer = null;
    }

    // ── ATC ─────────────────────────────────────────────────────────────────
    _bindATC() {
      this.el.addEventListener('click', async e => {
        const btn = e.target.closest(SELECTORS.atcBtn);
        if (!btn) return;
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        const original = btn.textContent.trim();
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = '…';
        try {
          if (window.MFBCart?.add) {
            await window.MFBCart.add({ id: variantId, quantity: 1 });
          } else {
            await this._fetchAddToCart(variantId);
          }
          btn.textContent = '✓ Added';
          setTimeout(() => {
            btn.textContent = original;
            btn.disabled = false;
          }, 2000);
        } catch {
          btn.textContent = 'Error — try again';
          setTimeout(() => {
            btn.textContent = original;
            btn.disabled = false;
          }, 2500);
        } finally {
          btn.setAttribute('aria-busy', 'false');
        }
      });
    }

    async _fetchAddToCart(variantId) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });
      if (!res.ok) throw new Error(await res.text());
      document.dispatchEvent(new CustomEvent('cart:open'));
      return res.json();
    }

    // ── Quick view ──────────────────────────────────────────────────────────
    _bindQuickView() {
      this.el.addEventListener('click', async e => {
        const btn = e.target.closest(SELECTORS.qvBtn);
        if (!btn) return;
        const handle = btn.dataset.handle;
        if (!handle) return;
        btn.disabled = true;
        try {
          const product = await fetch(`/products/${handle}.js`).then(r => r.json());
          this._openQV(product);
        } catch (err) {
          console.error('[PC] Quick view error:', err);
        } finally {
          btn.disabled = false;
        }
      });
    }

    _openQV(product) {
      const modalId = 'hbk-pc-qv-modal';
      let modal = document.getElementById(modalId);
      if (!modal) {
        modal = document.createElement('div');
        modal.id = modalId;
        modal.className = 'hbk-pc-qv-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-label', product.title);
        document.body.appendChild(modal);
      }

      const imgSrc  = product.images[0] ? product.images[0] : null;
      const imgHtml = imgSrc
        ? `<img src="${imgSrc}" alt="${product.title}" class="hbk-pc-qv-modal__img-el" loading="lazy">`
        : '';
      const price   = this._money(product.price);
      const varOpts = product.variants.map(v =>
        `<option value="${v.id}"${!v.available ? ' disabled' : ''}>${v.title}${!v.available ? ' — Sold out' : ''}</option>`
      ).join('');
      const selectEl = product.variants.length > 1
        ? `<select class="hbk-pc-qv-modal__variants" aria-label="Variant">${varOpts}</select>`
        : `<input type="hidden" class="hbk-pc-qv-modal__variants" value="${product.variants[0]?.id ?? ''}">`;

      modal.innerHTML = `
        <div class="hbk-pc-qv-modal__backdrop" data-pc-qv-close aria-hidden="true"></div>
        <div class="hbk-pc-qv-modal__content">
          <button class="hbk-pc-qv-modal__close" data-pc-qv-close type="button" aria-label="Close">×</button>
          <div class="hbk-pc-qv-modal__body">
            <div class="hbk-pc-qv-modal__img">${imgHtml}</div>
            <div class="hbk-pc-qv-modal__info">
              <h3 class="hbk-pc-qv-modal__title">${product.title}</h3>
              <p class="hbk-pc-qv-modal__price">${price}</p>
              ${selectEl}
              <button class="hbk-pc-qv-modal__atc" type="button">Add to cart</button>
              <a href="/products/${product.handle}" class="hbk-pc-qv-modal__full-link">View full details →</a>
            </div>
          </div>
        </div>
      `;

      modal.classList.add('hbk-pc-qv-modal--open');
      document.body.style.overflow = 'hidden';
      this._prevFocus = document.activeElement;

      modal.querySelectorAll('[data-pc-qv-close]').forEach(el => {
        el.addEventListener('click', () => this._closeQV(modal));
      });

      const atcBtn = modal.querySelector('.hbk-pc-qv-modal__atc');
      atcBtn?.addEventListener('click', async () => {
        const sel = modal.querySelector('.hbk-pc-qv-modal__variants');
        const vid = sel?.value;
        if (!vid) return;
        atcBtn.disabled = true;
        atcBtn.textContent = '…';
        try {
          await this._fetchAddToCart(vid);
          this._closeQV(modal);
        } catch {
          atcBtn.textContent = 'Error — try again';
          setTimeout(() => { atcBtn.textContent = 'Add to cart'; atcBtn.disabled = false; }, 2500);
        }
      });

      const escHandler = e => {
        if (e.key === 'Escape') { this._closeQV(modal); document.removeEventListener('keydown', escHandler); }
      };
      document.addEventListener('keydown', escHandler);

      atcBtn?.focus();
    }

    _closeQV(modal) {
      modal.classList.remove('hbk-pc-qv-modal--open');
      document.body.style.overflow = '';
      this._prevFocus?.focus();
    }

    _money(cents) {
      // Follow the store's ACTIVE currency (Shopify Markets), not a hardcoded EUR.
      var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
    }

    destroy() {
      this._stopAutoplay();
      this._io?.disconnect();
      if (this.track && this._drag) {
        const d = this._drag;
        this.track.removeEventListener('pointerdown', d.onDown);
        this.track.removeEventListener('click', d.onClick, true);
        this.track.removeEventListener('dragstart', d.onDragStart);
        window.removeEventListener('pointermove', d.onMove);
        window.removeEventListener('pointerup', d.onUp);
      }
    }
  }

  // ── Registry ──────────────────────────────────────────────────────────────
  const instances = new Map();

  function init(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new ProductCarousel(el));
    });
  }

  function destroy(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (!instances.has(el)) return;
      instances.get(el).destroy();
      instances.delete(el);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load',   e => init(e.target));
  document.addEventListener('shopify:section:unload', e => destroy(e.target));
})();
